import { AxiosResponse } from "axios";
import config from "../../shared/config/config";
import axios from "../../shared/utils/axios";
import axiosNoLoading from "../../shared/utils/axiosNoLoading";
import axiosNoToken from "../../shared/utils/axiosNoToken";
import {
  ActionResponse,
  FileUrlResponse,
  LoginDto,
  PermissionReturn,
  UserChangePasswordFromMailDTO,
  UserMemberDto,
} from "../../types/auth";
import {
  AlarmRequest,
  AlarmResponse,
  BasicReturnMsg,
  ReadAlarmResponse,
} from "../../types/common";
import axiosBlob from "../../shared/utils/axiosBlob";
import { Member } from "../../types/workgroup";
import { getWorkspaceCode } from "../../shared/utils/auth";

const GATEWAY_URL = config.servers.gateway.url;

interface AuthApi {
  sessionNew(): Promise<{ apiToken: string }>;
  sessionLogout(): Promise<any>;
  sessionLogin(userId: string, password: string, otp: string): Promise<any>;
  getLogInByUid(uid: string): Promise<LoginDto>;
  sendOTPEMail(userId: string, sessionUid?: string): Promise<void>;
  checkMailOtp(mailOTP: string, sessionUid?: string): Promise<any>;
  checkUserValidSignupUser(userId: string): Promise<any>;
  sendPasswordInit(userId: string): Promise<BasicReturnMsg>;
  getGoogleOTPInfo(userId: string): Promise<{ account: string; key: string }>;
  registerGoogleOTPInfo(userId: string): Promise<void>;
  loginGoogleOtp(req: { userId: string; otp: string }): Promise<any>;
  joinMember(uuuid: string, users: UserMemberDto[]): Promise<BasicReturnMsg>;
  getAlarmList(alarmRequest: AlarmRequest): Promise<AlarmResponse>;
  readAlarm(alarmRequest: AlarmRequest): Promise<AlarmRequest>;
  readAllAlarm(): Promise<ReadAlarmResponse>;
  getInformationFromUid(uid: string): Promise<ActionResponse>;
  getInformationFromSyncId(syncId: string): Promise<ActionResponse>;
  getFileUrlFromUid(req: {
    uid: string;
    token?: string;
  }): Promise<FileUrlResponse>;
  getFileUrlDownloadFromUid(req: {
    uid: string;
    token?: string;
  }): Promise<AxiosResponse>;
  getFileSimpleInfo(syncId: string): Promise<{
    contentName: string;
    fileSize: number;
    master: { userId: string; userName: string };
  }>;
  checkFilePWValid(reqDTO: {
    uuid: string;
    password: string;
  }): Promise<{ apiToken: string }>;
  changePasswordFromMail(
    req: UserChangePasswordFromMailDTO,
    initOtp: boolean
  ): Promise<Member>;
  startEsetAuthenticate(userId: string): Promise<{ pushChallengeId: string }>;
  checkEsetAuthenticate(pushChallengeId: string, userId: string): Promise<any>;
  ociManagePay(req: {
    userId: string;
    userEmail: string;
    alias: string;
    userName: string;
  }): Promise<{ redirectUrl: string }>;
  ociOnceLoginPay(req: {
    userId: string;
    userEmail: string;
    userName: string;
  }): Promise<{ redirectUrl: string }>;
  docHtmlDownload(
    uid: string,
    syncId: string,
    revisionNo: string,
    startDownload?: any
  ): Promise<any>;
  checkOTPPermission(req: {
    userId: string;
    uid: string;
    dsdCode?: string;
  }): Promise<PermissionReturn>;
  sendLiteOTP(req: { userId: string; sotp: string }): Promise<any>;
  checkLiteOTP(req: { mailOTP: string }): Promise<LoginDto>;
  getPasswdPolicy(): Promise<{ minLength: number; maxLength: number }>;
}

class AuthServerApi implements AuthApi {
  async sessionNew(): Promise<{ apiToken: string }> {
    const resp = await axiosNoToken.post<{ apiToken: string }>(
      `${GATEWAY_URL}/sessions/new`
    );
    return resp.data;
  }
  async sessionLogin(
    userId: string,
    password: string,
    otp: string
  ): Promise<any> {
    const params = new URLSearchParams();
    params.append("userId", userId);
    params.append("password", password);
    params.append("otp", otp);
    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/sessions/login`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async getLogInByUid(uid: string): Promise<LoginDto> {
    const params = new URLSearchParams();
    params.append("uid", uid);
    const resp = await axios.post<LoginDto>(
      `${GATEWAY_URL}/sessions/uid`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async sessionLogout(): Promise<void> {
    await axios.post<void>(`${GATEWAY_URL}/sessions/logout`);
  }
  async sendOTPEMail(userId: string, sessionUid?: string): Promise<void> {
    const params = new URLSearchParams();
    params.append("userId", userId);
    sessionUid && params.append("sessionUid", sessionUid);
    const resp = await axiosNoToken.post<void>(
      `${GATEWAY_URL}/sessions/otp/new`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async checkMailOtp(mailOTP: string, sessionUid?: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("mailOTP", mailOTP);
    sessionUid && params.append("sessionUid", sessionUid);
    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/sessions/otp/login`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async checkOTPPermission(req: {
    userId: string;
    uid: string;
    dsdCode?: string;
  }): Promise<PermissionReturn> {
    const params = new URLSearchParams();
    params.append("userId", req.userId);
    if (req.dsdCode) params.append("dsdCode", req.dsdCode);
    const resp = await axiosNoToken.post<PermissionReturn>(
      `${GATEWAY_URL}/papi/actionUrl/${req.uid}/permission`,
      { userId: req.userId, dsdCode: req.dsdCode ?? null }
    );
    return Promise.resolve(resp.data);
  }
  async joinMember(
    uuid: string,
    users: UserMemberDto[]
  ): Promise<BasicReturnMsg> {
    const resp = await axiosNoToken.post<BasicReturnMsg>(
      `${GATEWAY_URL}/papi/workgroups/members/update/`,
      { uuid, users }
    );
    return Promise.resolve(resp.data);
  }
  async checkUserValidSignupUser(userId: string): Promise<any> {
    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/papi/users/exists`,
      {
        userId,
      }
    );
    return Promise.resolve(resp.data);
  }
  async getGoogleOTPInfo(userId: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("userId", userId);

    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/sessions/otp/google/request`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async registerGoogleOTPInfo(userId: string): Promise<any> {
    const params = new URLSearchParams();
    params.append("userId", userId);

    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/sessions/otp/google/regist`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async loginGoogleOtp(req: { userId: string; otp: string }): Promise<any> {
    const params = new URLSearchParams();
    params.append("userId", req.userId);
    params.append("otp", req.otp);
    const resp = await axiosNoToken.post<any>(
      `${GATEWAY_URL}/sessions/otp/login/google`,
      params
    );
    return Promise.resolve(resp.data);
  }
  async sendPasswordInit(userId: string): Promise<BasicReturnMsg> {
    const params = new URLSearchParams();
    params.append("userId", userId);
    const resp = await axiosNoToken.get<BasicReturnMsg>(
      `${GATEWAY_URL}/papi/passwd/init/${userId}`
    );
    return Promise.resolve(resp.data);
  }
  async getAlarmList(alarmRequest: AlarmRequest): Promise<AlarmResponse> {
    const resp = await axiosNoLoading.post<AlarmResponse>(
      `${GATEWAY_URL}/wapi/dashboard/myAlarms`,
      alarmRequest
    );
    return Promise.resolve(resp.data);
  }
  async readAlarm(alarmRequest: AlarmRequest): Promise<AlarmResponse> {
    const resp = await axios.post<AlarmResponse>(
      `${GATEWAY_URL}/wapi/dashboard/readAlarm`,
      alarmRequest
    );
    return Promise.resolve(resp.data);
  }
  async readAllAlarm(): Promise<ReadAlarmResponse> {
    const resp = await axios.post<ReadAlarmResponse>(
      `${GATEWAY_URL}/wapi/dashboard/readAllAlarm`,
      {}
    );
    return Promise.resolve(resp.data);
  }
  async getInformationFromUid(uid: string): Promise<ActionResponse> {
    const resp = await axios.post<ActionResponse>(
      `${GATEWAY_URL}/papi/actionUrl/${uid}/info`
    );
    return Promise.resolve(resp.data);
  }
  async getInformationFromSyncId(syncId: string): Promise<ActionResponse> {
    const resp = await axios.get<ActionResponse>(
      `${GATEWAY_URL}/papi/actionUrl/syncId/${syncId}/info`
    );
    return Promise.resolve(resp.data);
  }
  async getFileUrlFromUid(req: {
    uid: string;
    token?: string;
  }): Promise<FileUrlResponse> {
    const params = new URLSearchParams();
    req.token && params.append("otp", req.token);
    const resp = await axios.get<FileUrlResponse>(
      `${GATEWAY_URL}/papi/file/${req.uid}`,
      {
        params: params,
      }
    );
    return Promise.resolve(resp.data);
  }
  async getFileUrlDownloadFromUid(req: {
    uid: string;
    token?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    req.token && params.append("otp", req.token);

    const resp = await axiosBlob.get<AxiosResponse>(
      `${GATEWAY_URL}/papi/file/${req.uid}/download`,
      {
        params: params,
        responseType: "blob",
        timeout: 0,
        maxContentLength: Infinity,
      }
    );
    if (resp.headers["content-disposition"]) {
      return Promise.resolve({ data: resp.data, headers: resp.headers });
    } else {
      return Promise.reject({});
    }
  }
  async getFileSimpleInfo(syncId: string): Promise<{
    contentName: string;
    fileSize: number;
    master: { userId: string; userName: string };
  }> {
    const resp = await axios.get<{
      contentName: string;
      fileSize: number;
      master: { userId: string; userName: string };
    }>(`${GATEWAY_URL}/papi/file/detail/${syncId}`);
    return Promise.resolve(resp.data);
  }
  async checkFilePWValid(reqDTO: {
    uuid: string;
    password: string;
  }): Promise<{ apiToken: string }> {
    const resp = await axios.post<{ apiToken: string }>(
      `${GATEWAY_URL}/papi/actionUrl/valid`,
      reqDTO
    );
    return Promise.resolve(resp.data);
  }
  async changePasswordFromMail(
    req: UserChangePasswordFromMailDTO,
    initOtp: boolean
  ): Promise<Member> {
    const params = new URLSearchParams();
    params.append("initOtp", initOtp.toString());
    const resp = await axios.post<Member>(
      `${GATEWAY_URL}/papi/passwd/update`,
      req,
      {
        params: {
          initOtp: initOtp,
        },
      }
    );

    return Promise.resolve(resp.data);
  }
  async startEsetAuthenticate(
    userId: string
  ): Promise<{ pushChallengeId: string }> {
    const resp = await axios.post<{ pushChallengeId: string }>(
      `${GATEWAY_URL}/sessions/otp/eset/start/authenticate?userId=${userId}`
    );
    return Promise.resolve(resp.data);
  }
  async checkEsetAuthenticate(
    pushChallengeId: string,
    userId: string
  ): Promise<any> {
    const resp = await axios.post<any>(
      `${GATEWAY_URL}/sessions/otp/eset/authenticate`,
      null,
      {
        params: {
          pushChallengeId: pushChallengeId,
          userId: userId,
        },
      }
    );
    return Promise.resolve(resp.data);
  }
  async ociOnceLoginPay(req: {
    userId: string;
    userEmail: string;
    userName: string;
  }): Promise<{ redirectUrl: string }> {
    const resp = await axios.post<{ redirectUrl: string }>(
      `${GATEWAY_URL}/wapi/tenancy/oncelogin/pay`,
      req
    );

    return Promise.resolve(resp.data);
  }
  async ociManagePay(req: {
    userId: string;
    userEmail: string;
    userName: string;
    alias: string;
  }): Promise<{ redirectUrl: string }> {
    const resp =
      req.alias !== "My Space"
        ? await axios.post<{ redirectUrl: string }>(
            `${GATEWAY_URL}/wapi/tenancy/oncelogin/manage`,
            req
          )
        : await axios.post<{ redirectUrl: string }>(
            `${GATEWAY_URL}/wapi/tenancy/oncelogin/pay`,
            req
          );

    return Promise.resolve(resp.data);
  }
  async docHtmlDownload(
    uid: string,
    syncId: string,
    revisionNo: string,
    startDownload: any
  ): Promise<any> {
    let resp: any;
    try {
      if (revisionNo) {
        resp = await axiosBlob.get<AxiosResponse>(
          `${GATEWAY_URL}/wapi/contents/download/${uid}/${syncId}/revisions/${revisionNo}/html`,
          {
            responseType: "blob",
            timeout: 0,
            maxContentLength: Infinity,
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.loaded) {
                startDownload(progressEvent.loaded);
              }
            },
          }
        );
      } else {
        resp = await axiosBlob.get<AxiosResponse>(
          `${GATEWAY_URL}/wapi/contents/download/${uid}/${syncId}/revisio
          ns/0/html`,
          {
            responseType: "blob",
            timeout: 0,
            maxContentLength: Infinity,
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.loaded) {
                startDownload(progressEvent.loaded);
              }
            },
          }
        );
      }

      return Promise.resolve({ data: resp.data, headers: resp.headers });
    } catch (error) {
      return Promise.resolve({ error: error });
    }
  }
  async sendLiteOTP(req: { userId: string; sotp: string }): Promise<any> {
    const resp = await axiosNoLoading.post<any>(
      `${GATEWAY_URL}/sessions/lite-external/ott/init`,
      null,
      {
        params: {
          userId: req.userId,
          sotp: req.sotp,
        },
      }
    );

    return Promise.resolve(resp.data);
  }
  async checkLiteOTP(req: { mailOTP: string }): Promise<LoginDto> {
    const resp = await axios.post<LoginDto>(
      `${GATEWAY_URL}/sessions/lite-external/ott`,
      { dsdCode: getWorkspaceCode() ?? undefined },
      {
        params: {
          mailOTP: req.mailOTP,
        },
      }
    );

    return Promise.resolve(resp.data);
  }
  async getPasswdPolicy(): Promise<{ minLength: number; maxLength: number }> {
    const resp = await axios.get<{ minLength: number; maxLength: number }>(
      `${GATEWAY_URL}/papi/passwd/policy`
    );

    return Promise.resolve(resp.data);
  }
}
const authApi: AuthApi = new AuthServerApi();
export default authApi;
